<template>
    <BaseModal :visible="visible" width="340px" content-class="agenda-reminder-dialog" @close="close">
        <div class="agenda-reminder-title">{{ $t('pages.module.AGENDA.reminder.reminders') }}</div>
        <div class="agenda-reminder-list">
            <div v-for="(rem, idx) in reminders" :key="idx" class="agenda-reminder-item">
                <div class="agenda-form-segmented">
                    <button class="agenda-form-segmented-btn" :class="{active: rem.type === 'relative'}" @click="rem.type !== 'relative' && (rem.type = 'relative', onTypeChange(rem))">{{ $t('pages.module.AGENDA.reminder.typeBefore') }}</button>
                    <button class="agenda-form-segmented-btn" :class="{active: rem.type === 'absolute'}" @click="rem.type !== 'absolute' && (rem.type = 'absolute', onTypeChange(rem))">{{ $t('pages.module.AGENDA.reminder.customDate') }}</button>
                </div>
                <el-select v-if="rem.type === 'relative'" v-model="rem.minutes_before" size="small" popper-class="agenda-app-popper" :teleported="true" style="width:100%">
                    <el-option v-for="m in presets" :key="m" :value="m" :label="formatLabel(m)" :disabled="isPresetUsed(m, idx)"/>
                </el-select>
                <DateTimePicker v-else v-model="rem.remind_at" type="datetime" :placeholder="$t('pages.module.AGENDA.reminder.timePlaceholder')" :min="nowMin" :max="eventMaxDate" :auto-modal="true"/>
                <button class="agenda-reminder-remove-btn" @click="removeReminder(idx)">{{ $t('pages.module.AGENDA.reminder.remove') }}</button>
            </div>
            <div v-if="reminders.length === 0" class="agenda-reminder-empty">{{ $t('pages.module.AGENDA.reminder.noReminders') }}</div>
        </div>
        <button v-if="reminders.length < 5" class="agenda-form-btn agenda-form-btn--add agenda-reminder-add-btn" @click="addReminder">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {{ $t('pages.module.AGENDA.reminder.addReminder') }}
        </button>
        <div class="agenda-reminder-error" v-if="errorMsg">{{ errorMsg }}</div>
        <div class="agenda-reminder-actions">
            <button class="agenda-reminder-btn agenda-reminder-btn--cancel" @click="close">{{ $t('pages.module.AGENDA.reminder.cancel') }}</button>
            <button class="agenda-reminder-btn agenda-reminder-btn--save" @click="save">{{ $t('pages.module.AGENDA.reminder.save') }}</button>
        </div>
    </BaseModal>
</template>

<script>
import DateTimePicker from '@/components/common/date-time-picker/DateTimePicker.vue'
import BaseModal from '@/components/common/base-modal/BaseModal.vue'

export default {
    name: 'AgendaReminderPicker',
    components: { DateTimePicker, BaseModal },
    data() {
        return {
            visible: false,
            event: null,
            reminders: [],
            errorMsg: '',
            nowMin: null,
        }
    },
    computed: {
        presets() {
            return [5, 10, 15, 30, 60, 120, 1440, 2880]
        },
        eventMaxDate() {
            if (!this.event || !this.event.event_at) return null
            return this.event.event_at.substring(0, 16).replace('T', ' ')
        },
    },
    methods: {
        formatLabel(minutes) {
            if (minutes < 60) return this.$t('pages.module.AGENDA.reminder.minutesBefore', {n: minutes})
            if (minutes < 1440) return this.$t('pages.module.AGENDA.reminder.hoursBefore', {n: minutes / 60})
            return this.$t('pages.module.AGENDA.reminder.daysBefore', {n: minutes / 1440})
        },
        onTypeChange(rem) {
            if (rem.type === 'relative') {
                const used = new Set(this.reminders.filter(r => r !== rem && r.type === 'relative').map(r => r.minutes_before))
                rem.minutes_before = this.presets.find(m => !used.has(m)) || this.presets[0]
                rem.remind_at = null
            } else {
                rem.minutes_before = null
                rem.remind_at = null
            }
        },
        isPresetUsed(minutes, currentIdx) {
            return this.reminders.some((r, i) => i !== currentIdx && r.type === 'relative' && r.minutes_before === minutes)
        },
        addReminder() {
            if (this.reminders.length >= 5) return
            const used = new Set(this.reminders.filter(r => r.type === 'relative').map(r => r.minutes_before))
            const first = this.presets.find(m => !used.has(m)) || this.presets[0]
            this.reminders.push({type: 'relative', minutes_before: first, remind_at: null})
        },
        removeReminder(idx) {
            this.reminders.splice(idx, 1)
        },
        _startNowTimer() {
            this._clearNowTimer()
            const msToNextMin = (60 - new Date().getSeconds()) * 1000
            this._nowTimeout = setTimeout(() => {
                this.nowMin = new Date()
                this._nowInterval = setInterval(() => { this.nowMin = new Date() }, 60000)
            }, msToNextMin)
        },
        _clearNowTimer() {
            clearTimeout(this._nowTimeout)
            clearInterval(this._nowInterval)
        },
        open(agendaEvent) {
            this.event = agendaEvent
            this.errorMsg = ''
            this.nowMin = new Date()
            this._startNowTimer()
            this.reminders = (agendaEvent.reminders || []).map(r => ({
                type: r.type || (r.minutes_before != null ? 'relative' : 'absolute'),
                minutes_before: r.minutes_before ?? null,
                remind_at: r.remind_at ? r.remind_at.substring(0, 16).replace('T', ' ') : null,
            }))
            this.visible = true
        },
        close() {
            this._clearNowTimer()
            this.visible = false
            this.event = null
            this.reminders = []
            this.errorMsg = ''
        },
        save() {
            this.errorMsg = ''
            const mapped = this.reminders.map(r => {
                if (r.type === 'relative') return {minutes_before: r.minutes_before}
                return {remind_at: r.remind_at}
            })
            for (const r of this.reminders) {
                if (r.type === 'absolute' && !r.remind_at) {
                    this.errorMsg = this.$t('pages.module.AGENDA.reminder.required')
                    return
                }
            }
            this.$emit('save', {event: this.event, reminders: mapped})
            this.close()
        },
    },
    beforeUnmount() {
        this._clearNowTimer()
    },
}
</script>
